import React, { useState, useEffect, useRef } from "react";
import { Modal, Layout } from "antd";

import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import ImageGrid from "./components/ImageGrid";
import Footer from "./components/Footer";
import CameraCaptureModal from "./components/CameraCaptureModal";
import UploadPreviewModal from "./components/UploadPreviewModal";
import CreateFolderModal from "./components/CreateFolderModal";
import { compressImage } from "./hooks/compressImage";
import {
    useUnsplashPhotos,
    usePexelsPhotos,
    useTenorGifs,
    usePixabayPhotos,
    useCollectionFiles,
    useCollectionFolders,
    useUploadFile,
    useCreateFolder,
    useUpdateFileArchiveStatus,
    useTrashFiles,
    usePermanentDeleteFile,
} from "./hooks/photo";

import { PEXELS_CONFIG, PIXABAY_CONFIG } from "./config/photo.config";
import { useCurrentUser } from "../../actions/auth";

import styles from "./imagePicker.module.css";
import { getPublicImageURL } from "../../_helpers/image";

const { Content } = Layout;

const DEFAULT_SEARCH = "nature";

function ImagePickerPanel({ open, onClose, onSelect, many = false }) {
    const [activeSource, setActiveSource] = useState("collection");
    const [searchQuery, setSearchQuery] = useState(DEFAULT_SEARCH);
    const [debouncedQuery, setDebouncedQuery] = useState(DEFAULT_SEARCH);
    const [page, setPage] = useState(1);
    const [selectedImages, setSelectedImages] = useState([]);
    const [folderStack, setFolderStack] = useState([
        { id: null, name: "My Collection" },
    ]);
    const [isMobile, setIsMobile] = useState(false);
    const isTrash = activeSource === "trash";

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 768px)");
        const handler = (e) => setIsMobile(e.matches);
        setIsMobile(mq.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);

    // Folder / upload state
    const [folderModalOpen, setFolderModalOpen] = useState(false);
    const [folderName, setFolderName] = useState("");
    const [uploadPreview, setUploadPreview] = useState(null);
    const [cameraModalVisible, setCameraModalVisible] = useState(false);

    // Refs
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const uploadTargetFolderRef = useRef(null);

    // Mutations
    const uploadMutation = useUploadFile();
    const createFolderMutation = useCreateFolder();
    const archiveFileMutation = useUpdateFileArchiveStatus();
    const permanentDeleteMutation = usePermanentDeleteFile();

    // User
    const { data: dataMe } = useCurrentUser();
    const userId = dataMe?.id || dataMe?.user?.id;

    // Queries
    const trashFilesQuery = useTrashFiles(userId, page);

    // Folder navigation
    const handleFolderClick = (folder) => {
        if (!folder?.id) return;
        setFolderStack((prev) => [
            ...prev,
            { id: folder.id, name: folder.name },
        ]);
    };
    const handleBreadcrumbClick = (index) => {
        setFolderStack((prev) => prev.slice(0, index + 1));
    };
    const activeFolderId =
        folderStack.length > 0 ? folderStack[folderStack.length - 1].id : null;

    // Source change (shared between Sidebar and Header)
    const handleSourceChange = (key) => {
        setActiveSource(key);
        setFolderStack([{ id: null, name: "My Collection" }]);
        setPage(1);
        if (key === "pixabay_png") {
            setSearchQuery((q) => (q.includes("png") ? q : `${q} png`));
        } else {
            setSearchQuery((q) => q.replace(" png", ""));
        }
    };

    // Normalize Directus files
    const normalizeCollectionImage = (img) => ({
        id: img.id,
        src: img?.data?.full_url || img?.url || getPublicImageURL(img.id),
        width: img.width,
        height: img.height,
        type: "collection",
        ...img,
    });

    // File upload
    const handleFileUpload = async (file) => {
        if (!file) return;
        try {
            const compressed = await compressImage(file);
            setUploadPreview({ original: file, compressed });
        } catch (err) {
            console.error("Compression failed, uploading original", err);
            uploadFile(file);
        }
    };
    const uploadFile = (file) => {
        uploadMutation.mutate({
            file,
            folder: uploadTargetFolderRef.current ?? null,
        });
    };

    // Proceed / Restore / Delete
    const handleProceed = () => {
        if (isTrash) return;
        if (selectedImages.length > 0 && onSelect) {
            try {
                onSelect(selectedImages);
            } catch (error) {
                console.error("Error calling onSelect:", error);
            }
        }
        onClose();
    };

    const handleRestore = () => {
        if (selectedImages.length === 0) return;
        archiveFileMutation.mutate(
            { fileId: selectedImages[0].id, archived: false },
            { onSuccess: () => setSelectedImages([]) }
        );
    };

    const handlePermanentDelete = () => {
        if (selectedImages.length === 0) return;
        permanentDeleteMutation.mutate(selectedImages[0].id, {
            onSuccess: () => setSelectedImages([]),
        });
    };

    const handleDeleteSelected = () => {
        const image = selectedImages[0];
        archiveFileMutation.mutate({ fileId: image.id, archived: true });
        setSelectedImages([]);
    };

    const isCollectionSelected =
        activeSource === "collection" && selectedImages.length > 0;

    useEffect(() => {
        setPage(1);
        setSelectedImages([]);
        if (activeSource === "collection") {
            setSearchQuery("");
            setDebouncedQuery("");
            return;
        }
        setSearchQuery((q) => q || DEFAULT_SEARCH);
        setDebouncedQuery((q) => q || DEFAULT_SEARCH);
    }, [activeSource]);

    useEffect(() => {
        if (!open) return;
        if (activeSource !== "collection") {
            setSearchQuery(DEFAULT_SEARCH);
            setDebouncedQuery(DEFAULT_SEARCH);
        }
    }, [open]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(searchQuery), 400);
        return () => clearTimeout(t);
    }, [searchQuery]);

    // Queries
    const foldersQuery = useCollectionFolders(userId, {
        parentId: activeFolderId,
    });
    const folders = React.useMemo(() => {
        const rawData = foldersQuery.data;
        if (Array.isArray(rawData)) return rawData;
        if (rawData?.data && Array.isArray(rawData.data)) return rawData.data;
        if (rawData?.folders && Array.isArray(rawData.folders))
            return rawData.folders;
        return [];
    }, [foldersQuery.data]);

    const collectionFilesQuery = useCollectionFiles(userId, {
        folderId: activeFolderId,
        page,
        perPage: 20,
    });
    const unsplashQuery = useUnsplashPhotos(debouncedQuery, page, {
        enabled: activeSource === "unsplash" && !!debouncedQuery,
    });
    const pexelsQuery = usePexelsPhotos(debouncedQuery, page, {
        enabled: activeSource === "pexels" && !!debouncedQuery,
    });
    const pixabayQuery = usePixabayPhotos(debouncedQuery, page, {
        enabled:
            (activeSource === "pixabay" || activeSource === "pixabay_png") &&
            !!debouncedQuery,
    });
    const tenorQuery = useTenorGifs(debouncedQuery, {
        enabled: activeSource === "gif" && !!debouncedQuery,
    });

    // Image data resolution
    let images = [];
    let loading = false;
    let totalPages = 0;

    switch (activeSource) {
        case "collection":
            images = collectionFilesQuery.data || [];
            loading = collectionFilesQuery.isLoading;
            break;
        case "unsplash":
            images = unsplashQuery.data?.results || [];
            loading = unsplashQuery.isLoading;
            totalPages = unsplashQuery.data?.total_pages || 0;
            break;
        case "pexels":
            images = pexelsQuery.data?.photos || [];
            loading = pexelsQuery.isLoading;
            totalPages = Math.ceil(
                (pexelsQuery.data?.total_results || 0) / PEXELS_CONFIG.PER_PAGE
            );
            break;
        case "pixabay":
        case "pixabay_png":
            images = pixabayQuery.data?.hits || [];
            loading = pixabayQuery.isLoading;
            totalPages = Math.ceil(
                (pixabayQuery.data?.totalHits || 0) / PIXABAY_CONFIG.PER_PAGE
            );
            break;
        case "gif":
            images = tenorQuery.data?.results || [];
            loading = tenorQuery.isLoading;
            totalPages = 5;
            break;
        case "trash":
            images = trashFilesQuery.data || [];
            loading = trashFilesQuery.isLoading;
            break;
        default:
            break;
    }

    return (
        <>
            <UploadPreviewModal
                open={!!uploadPreview}
                uploadPreview={uploadPreview}
                onClose={() => setUploadPreview(null)}
                onUploadOriginal={() => {
                    uploadFile(uploadPreview.original);
                    setUploadPreview(null);
                }}
                onUploadCompressed={() => {
                    uploadFile(uploadPreview.compressed);
                    setUploadPreview(null);
                }}
            />

            <Modal
                open={open}
                onCancel={onClose}
                width={isMobile ? "100%" : 1100}
                centered={!isMobile}
                destroyOnHidden
                maskClosable={true}
                footer={
                    <Footer
                        isMobile={isMobile}
                        isTrash={isTrash}
                        activeSource={activeSource}
                        selectedImages={selectedImages}
                        page={page}
                        totalPages={totalPages}
                        isCollectionSelected={isCollectionSelected}
                        onClose={onClose}
                        onProceed={handleProceed}
                        onRestore={handleRestore}
                        onDeleteSelected={handleDeleteSelected}
                        onPermanentDelete={handlePermanentDelete}
                        onPageChange={setPage}
                    />
                }
                closable={false}
                styles={{
                    container: isMobile
                        ? {
                              padding: "12px",
                              borderRadius: "20px 20px 0 0",
                          }
                        : {},
                    body: {
                        padding: 0,
                        height: isMobile ? "calc(90dvh - 80px)" : 590,
                        // overflow: "hidden",
                        // display: "flex",
                        maxWidth: "100vw",
                        overflowY: "auto",
                        width: "100%",
                    },
                    footer: isMobile
                        ? {}
                        : {
                              padding: "0 24px",
                              marginTop: 0,
                          },
                }}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        handleFileUpload(file);
                        e.target.value = "";
                    }}
                />

                <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: "none" }}
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        handleFileUpload(file);
                        e.target.value = "";
                    }}
                />

                <Layout className={styles.modalLayout}>
                    {!isMobile && (
                        <Sidebar
                            activeSource={activeSource}
                            onChange={handleSourceChange}
                        />
                    )}
                    <Content className={styles.content}>
                        <Header
                            isMobile={isMobile}
                            activeSource={activeSource}
                            onSourceChange={handleSourceChange}
                            showSearch={
                                activeSource !== "collection" && !isTrash
                            }
                            isCollection={activeSource === "collection"}
                            value={searchQuery}
                            folders={folders}
                            folderStack={folderStack}
                            activeFolderId={activeFolderId}
                            onFolderClick={handleFolderClick}
                            onBreadcrumbClick={handleBreadcrumbClick}
                            onChange={(val) => {
                                setSearchQuery(val);
                                setPage(1);
                            }}
                            page={page}
                            totalPages={totalPages}
                            onPageChange={setPage}
                            onUpload={() => {
                                uploadTargetFolderRef.current = activeFolderId;
                                fileInputRef.current?.click();
                            }}
                            onCamera={() => setCameraModalVisible(true)}
                            onCreateFolder={() => setFolderModalOpen(true)}
                            onClose={onClose}
                        />

                        <div className={styles.gridWrapper}>
                            <ImageGrid
                                images={
                                    activeSource === "collection"
                                        ? images.map(normalizeCollectionImage)
                                        : images
                                }
                                loading={loading}
                                selectedImages={selectedImages}
                                onSelect={(img) => {
                                    setSelectedImages((prev) => {
                                        const imgId =
                                            img?.id ||
                                            img?.urls?.raw ||
                                            img?.src;
                                        const exists = prev.find(
                                            (p) =>
                                                (p?.id ||
                                                    p?.urls?.raw ||
                                                    p?.src) === imgId
                                        );

                                        if (exists) {
                                            return prev.filter(
                                                (p) =>
                                                    (p?.id ||
                                                        p?.urls?.raw ||
                                                        p?.src) !== imgId
                                            );
                                        }

                                        if (many) {
                                            return [...prev, img];
                                        }

                                        return [img];
                                    });
                                }}
                            />
                        </div>
                    </Content>
                </Layout>
            </Modal>

            <CreateFolderModal
                open={folderModalOpen}
                folderName={folderName}
                onFolderNameChange={setFolderName}
                onCancel={() => {
                    setFolderModalOpen(false);
                    setFolderName("");
                }}
                onOk={() => {
                    if (!folderName.trim()) return;
                    createFolderMutation.mutate(
                        {
                            name: folderName.trim(),
                            parent: activeFolderId ?? null,
                        },
                        {
                            onSuccess: () => {
                                setFolderModalOpen(false);
                                setFolderName("");
                                foldersQuery.refetch();
                            },
                        }
                    );
                }}
                confirmLoading={createFolderMutation.isLoading}
            />

            <CameraCaptureModal
                open={cameraModalVisible}
                onClose={() => setCameraModalVisible(false)}
                onCaptureComplete={(file) => {
                    handleFileUpload(file);
                }}
            />
        </>
    );
}

export default ImagePickerPanel;
